import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import TaskTable, { PageHeader, TASK_COLUMNS } from "../components/TaskTable";
import TaskDrawer from "../components/TaskDrawer";
import { useData } from "../lib/DataContext";
import { useAuth } from "../lib/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PriorityBadge, StatusBadge, fmtDate } from "../lib/ui";
import { toast } from "sonner";
import { LayoutGrid, Table2, Download, Filter, X, Plus, Bell, Save, Bookmark, Columns3, Trash2, Star, Eye, ListOrdered, Settings, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "../components/ui/dropdown-menu";
import { Checkbox } from "../components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import QuickCreateTask from "../components/QuickCreateTask";

const KANBAN_COLS = ["Not Started", "Open", "In Progress", "Waiting", "Blocked", "Review", "Completed"];

export default function Tasks() {
  const [params, setParams] = useSearchParams();
  const { userName, deptName, departments, projects, users, favourites, toggleFavourite } = useData();
  const { user } = useAuth();
  const [view, setView] = useState("table");
  const [data, setData] = useState({ items: [], total: 0 });
  const [openId, setOpenId] = useState(params.get("open"));
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ by: "created_at", order: -1 });
  const [showCreate, setShowCreate] = useState(false);
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();
  const [hiddenCols, setHiddenCols] = useState([]);
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState("");
  const [defaultViewId, setDefaultViewId] = useState(null);
  const [viewResolved, setViewResolved] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [viewName, setViewName] = useState("");
  const [shareView, setShareView] = useState(false);
  const [showFav, setShowFav] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const manual = sort.by === "sequence_order";
  const toggleManual = () => setSort(manual ? { by: "created_at", order: -1 } : { by: "sequence_order", order: 1 });
  const move = async (id, direction) => {
    try { await api.post(`/tasks/${id}/reorder`, { direction }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot move"); }
  };
  const dropReorder = async (movedId, targetId) => {
    try { await api.post(`/tasks/${movedId}/reorder`, { target_id: targetId }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot reorder"); }
  };

  const loadViews = () => api.get("/saved-views").then((r) => setViews(r.data)).catch(() => {});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [v, d] = await Promise.all([api.get("/saved-views"), api.get("/auth/preferences/task-default-view")]);
        if (cancelled) return;
        const list = v.data || [];
        setViews(list);
        const dv = d.data?.view_id || null;
        setDefaultViewId(dv);
        const explicit = ["open", "priority", "execution_status", "overdue", "blocked", "due", "project_id"].some((k) => params.get(k));
        if (dv && !explicit) {
          const found = list.find((x) => x.id === dv);
          if (found) {
            setActiveView(dv);
            applyViewConfig(found);
          } else {
            // default view deleted/revoked — clear reference, keep normal behavior
            setDefaultViewId(null);
            api.put("/auth/preferences/task-default-view", { view_id: null }).catch(() => {});
          }
        }
      } catch {}
      if (!cancelled) setViewResolved(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyViewConfig = (v) => {
    if (!v) return;
    const f = v.filters || {};
    setFilters(f.filters || { search: "" });
    if (f.sort) setSort(f.sort);
    if (f.view) setView(f.view);
    setHiddenCols(f.hiddenCols || []);
    setPage(1);
  };
  const applyView = (id) => {
    setActiveView(id);
    applyViewConfig(views.find((x) => x.id === id));
  };
  const toggleDefault = async (id) => {
    const nv = defaultViewId === id ? null : id;
    try {
      await api.put("/auth/preferences/task-default-view", { view_id: nv });
      setDefaultViewId(nv);
      toast.success(nv ? "Default view set" : "Default view removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveView = async () => {
    if (!viewName.trim()) return toast.error("Name required");
    await api.post("/saved-views", { name: viewName.trim(), shared: shareView,
      filters: { filters, sort, view, hiddenCols } });
    toast.success("View saved"); setShowSave(false); setViewName(""); setShareView(false); loadViews();
  };
  const deleteView = async (id) => {
    await api.delete(`/saved-views/${id}`); toast.success("View deleted");
    if (activeView === id) setActiveView("");
    if (defaultViewId === id) setDefaultViewId(null);
    loadViews();
  };
  const toggleCol = (key) => setHiddenCols((h) => (h.includes(key) ? h.filter((k) => k !== key) : [...h, key]));
  const [filters, setFilters] = useState({
    search: "", department_id: "", project_id: params.get("project_id") || "", priority: "", execution_status: "",
    current_action_owner_id: "", health: "",
    overdue: params.get("overdue") ? true : undefined, blocked: params.get("blocked") ? true : undefined,
    due: params.get("due") || undefined,
  });
  const canBulk = ["master_admin", "admin", "manager"].includes(user?.role);

  const load = useCallback(async () => {
    const q = { page, page_size: 50, sort: sort.by, order: sort.order };
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== undefined) q[k] = v; });
    if (params.get("priority")) q.priority = params.get("priority");
    if (params.get("execution_status")) q.execution_status = params.get("execution_status");
    if (showFav) q.favourite = true;
    if (showHidden) q.hidden = true;
    const { data } = await api.get("/tasks", { params: q });
    setData(data);
  }, [filters, page, params, sort, showFav, showHidden]);

  useEffect(() => { if (viewResolved) load(); }, [load, viewResolved]);

  useEffect(() => {
    const fetchUnread = () => api.get("/notifications", { params: { unread_only: true } }).then((r) => setUnread(r.data.length)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, []);

  const setF = (k, v) => { setFilters((p) => ({ ...p, [k]: v === "all" ? "" : v })); setPage(1); };

  const bulk = async (action, value) => {
    if (selected.length === 0) return toast.error("Select tasks first");
    await api.post("/tasks/bulk", { task_ids: selected, action, value });
    toast.success(`${selected.length} tasks updated`);
    setSelected([]); load();
  };

  const exportCsv = async () => {
    const res = await api.get("/export/tasks", { params: { fmt: "csv" }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "tasks.csv"; a.click();
  };
  const exportXlsx = async () => {
    const res = await api.get("/export/tasks", { params: { fmt: "excel" }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "tasks.xlsx"; a.click();
  };

  const toggleSort = (key) =>
    setSort((s) => (s.by === key ? { by: key, order: s.order === -1 ? 1 : -1 } : { by: key, order: key === "created_at" ? -1 : 1 }));

  const kanban = KANBAN_COLS.reduce((acc, c) => {    acc[c] = data.items.filter((t) => {
      if (c === "Blocked") return t.blocked;
      if (c === "Review") return ["Pending Review", "Pending Approval"].includes(t.review_status);
      return t.execution_status === c && !t.blocked;
    });
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Tasks" subtitle={`${data.total} tasks`} headerExtra={
        <div className="relative flex-1 min-w-40">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input data-testid="task-search" placeholder="Search tasks…" value={filters.search} onChange={(e) => setF("search", e.target.value)} className="h-9 w-full rounded-lg text-sm pl-9" />
        </div>
      }>
        <button
          data-testid="quick-create-task-button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg px-3 py-2 transition-colors"
        >
          <Plus size={16} /> <span className="hidden sm:inline">New Task</span>
        </button>
        <div className="flex items-center rounded-lg border bg-white p-0.5">
          <button data-testid="view-table" onClick={() => setView("table")} className={`p-1.5 rounded ${view === "table" ? "bg-slate-100" : ""}`}><Table2 size={16} /></button>
          <button data-testid="view-kanban" onClick={() => setView("kanban")} className={`p-1.5 rounded ${view === "kanban" ? "bg-slate-100" : ""}`}><LayoutGrid size={16} /></button>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="task-settings-menu" title="Settings" className={`inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${showHidden || manual ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}><Settings size={16} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem data-testid="export-csv" onClick={exportCsv}><Download size={15} className="mr-2" /> Export CSV</DropdownMenuItem>
            <DropdownMenuItem data-testid="export-excel" onClick={exportXlsx}><Download size={15} className="mr-2" /> Export Excel</DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="columns-button"><Columns3 size={15} className="mr-2" /> Columns</DropdownMenuSubTrigger>
              <DropdownMenuSubContent data-testid="columns-panel" className="w-48">
                {TASK_COLUMNS.map((c) => (
                  <DropdownMenuCheckboxItem key={c.key} checked={!hiddenCols.includes(c.key)} onSelect={(e) => e.preventDefault()} onCheckedChange={() => toggleCol(c.key)} data-testid={`col-toggle-${c.key}`}>
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="saved-view-select"><Bookmark size={15} className="mr-2" /> Saved views</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-56">
                <DropdownMenuItem disabled className="text-slate-400">— No view —</DropdownMenuItem>
                {views.map((v) => (
                  <DropdownMenuItem key={v.id} data-testid={`saved-view-item-${v.id}`} onClick={() => applyView(v.id)}>
                    <span className="truncate">{v.name}{v.shared ? " (shared)" : ""}</span>
                    <span className="ml-auto flex items-center gap-1.5 pl-2">
                      {activeView === v.id && <span className="h-2 w-2 rounded-full bg-blue-600" />}
                      <button
                        data-testid={`set-default-${v.id}`}
                        title={defaultViewId === v.id ? "Remove default" : "Set as default"}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDefault(v.id); }}
                        className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        <Star size={12} className={defaultViewId === v.id ? "fill-amber-500 text-amber-500" : "text-slate-300 hover:text-amber-400"} />
                      </button>
                    </span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="save-view-button" onClick={() => setShowSave(true)}><Save size={15} className="mr-2" /> Save current view</DropdownMenuItem>
                {activeView && <DropdownMenuItem data-testid="delete-view" className="text-red-600" onClick={() => deleteView(activeView)}><Trash2 size={15} className="mr-2" /> Delete current view</DropdownMenuItem>}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="filter-show-hidden" onClick={() => { setShowHidden((s) => !s); setPage(1); }}>
              <Eye size={15} className="mr-2" /> Show Hidden
              {showHidden && <span className="ml-auto text-[10px] font-semibold text-blue-600 bg-blue-50 rounded px-1.5 py-0.5">ON</span>}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="toggle-manual-order" onClick={toggleManual}>
              <ListOrdered size={15} className="mr-2" /> Manual order
              {manual && <span className="ml-auto text-[10px] font-semibold text-blue-600 bg-blue-50 rounded px-1.5 py-0.5">ON</span>}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          data-testid="header-notifications"
          onClick={() => nav("/notifications")}
          className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-600"
        >
          <Bell size={18} />
          {unread > 0 && <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />}
        </button>
      </PageHeader>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <FilterSelect label="Department" value={filters.department_id} onChange={(v) => setF("department_id", v)} options={departments.map((d) => ({ v: d.id, l: d.name }))} testid="filter-department" />
        <FilterSelect label="Project" value={filters.project_id} onChange={(v) => setF("project_id", v)} options={projects.map((p) => ({ v: p.id, l: p.name }))} />
        <FilterSelect label="Owner" value={filters.current_action_owner_id} onChange={(v) => setF("current_action_owner_id", v)} options={users.map((u) => ({ v: u.id, l: u.full_name }))} testid="filter-owner" />
        <FilterSelect label="Priority" value={filters.priority} onChange={(v) => setF("priority", v)} options={["Critical","High","Medium","Low"].map((p) => ({ v: p, l: p }))} testid="filter-priority" />
        <FilterSelect label="Status" value={filters.execution_status} onChange={(v) => setF("execution_status", v)} options={["Not Started","Open","In Progress","Waiting","On Hold","Completed"].map((s) => ({ v: s, l: s }))} testid="filter-status" />
        <FilterSelect label="Health" value={filters.health} onChange={(v) => setF("health", v)} options={["Healthy","At Risk","Overdue","Blocked"].map((h) => ({ v: h, l: h }))} testid="filter-health" />
        <button data-testid="filter-favourites" onClick={() => { setShowFav((s) => !s); setPage(1); }}
          className={`flex items-center gap-1 h-9 px-2.5 rounded-md border text-sm ${showFav ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
          <Star size={14} className={showFav ? "fill-amber-500 text-amber-500" : ""} /> Favourites
        </button>
        {(params.get("overdue") || params.get("blocked") || params.get("due") || params.get("priority")) && (
          <Button variant="ghost" size="sm" onClick={() => { setParams({}); setFilters({ search: "" }); }}><X size={14} className="mr-1" /> Clear</Button>
        )}
      </div>

      {/* Bulk bar */}
      {canBulk && selected.length > 0 && (
        <div className="flex items-center gap-2 mb-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="bulk-bar">
          <span className="text-sm font-medium text-blue-800">{selected.length} selected</span>
          <Select onValueChange={(v) => bulk("priority", v)}><SelectTrigger className="w-36 h-8"><SelectValue placeholder="Set Priority" /></SelectTrigger>
            <SelectContent>{["Critical","High","Medium","Low"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
          <Select onValueChange={(v) => bulk("status", v)}><SelectTrigger className="w-36 h-8"><SelectValue placeholder="Set Status" /></SelectTrigger>
            <SelectContent>{["Open","In Progress","Waiting","Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" variant="destructive" onClick={() => bulk("archive")} data-testid="bulk-archive">Archive</Button>
          {showHidden
            ? <Button size="sm" variant="outline" onClick={() => bulk("unhide")} data-testid="bulk-unhide">Unhide</Button>
            : <Button size="sm" variant="outline" onClick={() => bulk("hide")} data-testid="bulk-hide">Hide</Button>}
          <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button>
        </div>
      )}

      {view === "table" ? (
        <TaskTable tasks={data.items} onOpen={setOpenId} selectable={canBulk} selected={selected} onSelect={setSelected}
          sortBy={sort.by} sortOrder={sort.order} onSort={toggleSort} hiddenCols={hiddenCols}
          favourites={favourites.task} onToggleFav={(id) => toggleFavourite("task", id)}
          reorderable={manual && view === "table"} onMoveUp={(id) => move(id, "up")} onMoveDown={(id) => move(id, "down")} onDropReorder={dropReorder} />
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {KANBAN_COLS.map((col) => (
            <div key={col} data-testid={`kanban-column-${col.toLowerCase().replace(/ /g, "-")}`} className="w-72 shrink-0">
              <div className="flex items-center justify-between px-1 mb-2">
                <span className="text-sm font-semibold text-slate-700">{col}</span>
                <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2">{kanban[col].length}</span>
              </div>
              <div className="space-y-2">
                {kanban[col].map((t) => (
                  <button key={t.id} data-testid={`kanban-card-${t.human_id}`} onClick={() => setOpenId(t.id)}
                    className="w-full text-left bg-white border rounded-lg p-3 hover:shadow-md transition-shadow">
                    <div className="font-mono text-[10px] text-slate-400">{t.human_id}</div>
                    <div className="text-sm font-medium text-slate-800 mt-0.5 line-clamp-2">{t.title}</div>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <PriorityBadge value={t.priority} />
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                      <span>{userName(t.current_action_owner_id)}</span>
                      <span className={t.days_overdue > 0 ? "text-red-600" : ""}>{fmtDate(t.current_due_date)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {openId && <TaskDrawer taskId={openId} onClose={() => { setOpenId(null); params.delete("open"); setParams(params); }} onChanged={load} />}
      {showCreate && <QuickCreateTask onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {showSave && (
        <Dialog open onOpenChange={() => setShowSave(false)}>
          <DialogContent data-testid="save-view-dialog">
            <DialogHeader><DialogTitle>Save Current View</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="View name" value={viewName} onChange={(e) => setViewName(e.target.value)} data-testid="save-view-name" />
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={shareView} onCheckedChange={setShareView} data-testid="save-view-share" /> Share with team</label>
              <div className="text-xs text-slate-500">Saves current filters, sort, {view} layout and column choices.</div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowSave(false)}>Cancel</Button><Button onClick={saveView} data-testid="save-view-confirm">Save View</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <Select value={value || "all"} onValueChange={onChange}>
      <SelectTrigger className="w-40 h-9" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {label}</SelectItem>
        {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
